/**
 * @module AdapterList
 * @package @geenius/adapters/react
 * @description Renders the searchable adapter grid for the React variant. The
 * list consumes provider status from context and delegates selection events to
 * the hosting page or dashboard shell.
 */

import type { AdapterDomain, AdapterStatusType } from "@geenius/adapters";
import { ADAPTER_DOMAINS, DOMAIN_LABELS } from "@geenius/adapters";
import type { JSX } from "react";
import { useMemo, useState } from "react";
import { useAdapters } from "../hooks";
import { cn } from "../lib/utils";
import { AdapterCard } from "./AdapterCard";

/**
 * Props for rendering the React adapter list and optional status filter controls.
 *
 * @property onSelect Optional callback invoked when a domain card is selected.
 * @property filterStatus Optional status filter applied before rendering the grid.
 * @property className Optional class name merged into the outer wrapper.
 */
export interface AdapterListProps {
  onSelect?: (domain: AdapterDomain) => void;
  filterStatus?: AdapterStatusType;
  className?: string;
}

/**
 * Renders the searchable React adapter list backed by shared provider state.
 *
 * @param props List props including selection callback, status filtering, and wrapper class names.
 * @returns The adapter grid or an empty/loading state when no entries are available.
 */
export function AdapterList({
  onSelect,
  filterStatus,
  className,
}: AdapterListProps): JSX.Element {
  const { statuses, isLoading } = useAdapters();
  const [search, setSearch] = useState("");

  const filteredDomains = useMemo(() => {
    let domains = [...ADAPTER_DOMAINS];

    if (search) {
      const query = search.toLowerCase();
      domains = domains.filter((domain) => {
        const label = (DOMAIN_LABELS[domain] ?? domain).toLowerCase();
        const provider = statuses[domain]?.provider?.toLowerCase() ?? "";
        return (
          label.includes(query) ||
          provider.includes(query) ||
          domain.includes(query)
        );
      });
    }

    if (filterStatus) {
      domains = domains.filter(
        (domain) => statuses[domain]?.status === filterStatus,
      );
    }

    return domains;
  }, [search, filterStatus, statuses]);

  if (isLoading) {
    return (
      <div
        className={cn("grid gap-3", className)}
        role="status"
        aria-label="Loading adapters"
      >
        {[1, 2, 3, 4].map((index) => (
          <div
            key={index}
            className="rounded-lg border border-border bg-card p-5 shadow-sm"
          >
            <div className="flex items-start gap-4">
              <div className="h-12 w-12 rounded-md border border-border bg-muted" />
              <div className="flex-1 space-y-3 pt-1">
                <div className="h-3 w-24 rounded-md bg-muted" />
                <div className="h-2 w-48 rounded-md bg-muted/70" />
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className={className}>
      <div className="mb-4">
        <input
          type="text"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Search adapters..."
          aria-label="Search adapters"
          className="w-full rounded-md border border-input bg-background px-4 py-3 text-sm text-foreground shadow-sm outline-none transition-colors placeholder:text-muted-foreground focus:border-primary focus:ring-2 focus:ring-primary/20 focus-visible:ring-ring focus-visible:ring-offset-2 motion-reduce:transition-none"
        />
      </div>

      {filteredDomains.length === 0 ? (
        <div className="rounded-lg border border-dashed border-border bg-muted/40 p-8 text-center shadow-sm">
          <p className="text-sm font-semibold text-foreground">
            {search
              ? "No adapters match your search"
              : "No adapters configured"}
          </p>
          <p className="mx-auto mt-2 max-w-sm text-xs text-muted-foreground">
            {search
              ? "Clear the search to return to the full adapter list."
              : "Check the current filters or configure an adapter from the overview page."}
          </p>
          {search && (
            <button
              type="button"
              onClick={() => setSearch("")}
              className="mt-4 rounded-md border border-border bg-background px-4 py-2 text-sm font-semibold text-foreground transition-colors hover:border-primary/30 hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 motion-reduce:transition-none"
            >
              Clear search
            </button>
          )}
        </div>
      ) : (
        <ul
          className="m-0 grid list-none gap-3 p-0 sm:grid-cols-2"
          aria-label="Adapter list"
        >
          {filteredDomains.map((domain) => (
            <li key={domain}>
              <AdapterCard
                domain={domain}
                status={statuses[domain]}
                onClick={onSelect ? () => onSelect(domain) : undefined}
              />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
